import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
} from '@mui/material';
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import Layout from '../../components/layout/Layout';
import barcodeSettingsService from '../../services/barcodeSettingsService';
import companySettingsService from '../../services/companySettingsService';
import { getCurrentUser } from '../../services/authService';
import { toast } from 'react-hot-toast';

const MM_TO_PX = 3.78;

/** Paper size options (width mm, height mm) for preview and print */
const PAPER_SIZES = [
  { value: 'A4', label: 'A4 (210 × 297 mm)', widthMm: 210, heightMm: 297 },
  { value: 'A5', label: 'A5 (148 × 210 mm)', widthMm: 148, heightMm: 210 },
  { value: 'Letter', label: 'Letter (216 × 279 mm)', widthMm: 215.9, heightMm: 279.4 },
  { value: 'Legal', label: 'Legal (216 × 356 mm)', widthMm: 215.9, heightMm: 355.6 },
  { value: 'Roll72', label: 'Roll 72 mm (e.g. 72×10 tag)', widthMm: 72, heightMm: 297 },
  { value: 'Roll80', label: 'Roll 80 mm (printable ≤108 mm)', widthMm: 80, heightMm: 297 },
  { value: 'Roll100', label: 'Roll 100 mm (printable ≤108 mm)', widthMm: 100, heightMm: 297 },
];

const SCHEME_OPTIONS = [
  { value: '1x1', label: '1x1' },
  { value: '3x3', label: '3x3' },
  { value: '4x4', label: '4x4' },
  { value: '5x5', label: '5x5' },
];

/** Derive number of columns from scheme name (e.g. 1x1 → 1, 4x4 → 4) so layout reflects scheme. */
const schemeToColumns = (schemeName) => {
  if (!schemeName) return null;
  const m = String(schemeName).match(/^(\d+)x\d+$/i);
  return m ? Math.max(1, parseInt(m[1], 10)) : null;
};

const FIELD_OPTIONS = [
  { key: 'QR', label: 'Barcode / QR Image' },
  { key: 'COMPANY_NAME', label: 'Company Name' },
  { key: 'NAME', label: 'Product Name' },
  { key: 'SKU', label: 'SKU (Full)' },
  { key: 'SKU_LAST3', label: 'SKU (Last 4 Digits)' },
  { key: 'SUPPLIER_CODE', label: 'Supplier Code' },
  { key: 'PRODUCT_CODE', label: 'Product Code (Remark)' },
  { key: 'BARCODE', label: 'Barcode Number' },
  { key: 'MRP', label: 'MRP' },
  { key: 'SELLING_PRICE', label: 'Selling Price (DRP)' },
  { key: 'DATE_MMYY', label: 'Date (MMYY) - Updated At' },
  { key: 'STOCK_DATE_MMYY', label: 'Stock-In Date (MMYY)' },
  { key: 'HSN_CODE', label: 'HSN Code' },
  { key: 'CATEGORY', label: 'Category' },
];

/** One-click layouts — users pick fields in bulk instead of one-by-one. */
const FIELD_PRESETS = [
  { id: 'standard', label: 'Barcode + Name + MRP', keys: ['QR', 'NAME', 'MRP'] },
  { id: 'retail', label: 'Barcode + Name + SKU + MRP', keys: ['QR', 'NAME', 'SKU', 'MRP'] },
  { id: 'price', label: 'Barcode + MRP + Price', keys: ['QR', 'MRP', 'SELLING_PRICE'] },
  { id: 'detail', label: 'Full retail', keys: ['QR', 'NAME', 'SKU', 'BARCODE', 'MRP', 'SELLING_PRICE'] },
];

const defaultSettings = {
  schemeName: '4x4',
  paperSize: 'A4',
  labelWidthMm: 39,
  labelHeightMm: 35,
  barcodeWidthMm: 25,
  barcodeHeightMm: 15,
  marginLeftMm: 0,
  marginTopMm: 1,
  // Keep bottom/right symmetric with top/left by default.
  marginBottomMm: 1,
  marginRightMm: 0,
  horizontalGapMm: 0,
  verticalGapMm: 1,
  noOfColumns: 5,
  currency: 'Rs.',
  decimalsForPrice: 0,
  codeType: 'BARCODE',
  showBarcodeNumber: true,
  visibleFields: 'NAME,BARCODE,MRP',
  fieldLayout: null,
  fontSizePt: 8,
  fieldSettings: null,
};

const defaultFieldSetting = (fieldKey, order = 0) => ({
  fieldKey,
  show: true,
  left: 4,
  width: 35,
  top: 7,
  height: 15,
  fontSize: 8,
  alignment: 'Centre',
  order,
});

/** Stagger text fields vertically so a preset does not stack everything on top. */
const presetTopMm = (fieldKey, indexAmongText) => {
  if (fieldKey === 'QR') return 1;
  return 8 + indexAmongText * 6;
};

const buildDraftFieldMap = (storedList) => {
  const stored = Array.isArray(storedList) ? storedList : [];
  const draft = {};
  FIELD_OPTIONS.forEach((opt, idx) => {
    const found = stored.find((f) => f.fieldKey === opt.key);
    draft[opt.key] = found
      ? { ...defaultFieldSetting(opt.key, found.order ?? idx), ...found, show: found.show !== false }
      : { ...defaultFieldSetting(opt.key, idx), show: false };
  });
  return draft;
};

const draftMapToFieldList = (draft) =>
  FIELD_OPTIONS.map((opt, idx) => draft[opt.key])
    .filter((f) => f && f.show === true)
    .map((f, i) => ({ ...f, order: i }));

const sampleProduct = {
  id: 'SAMPLE',
  name: 'Sample product',
  sku: 'SKU001',
  barcode: 'BC001',
  mrp: 100,
  sellingPrice: 100,
  hsnCode: 'HSN001',
  category: { name: 'Category' },
  updatedAt: new Date().toISOString(),
  stockInDate: new Date().toISOString(),
};

const mapFormatToSettings = (f) => ({
  ...defaultSettings,
  id: f.id,
  formatName: f.formatName,
  paperSize: f.paperSize || defaultSettings.paperSize,
  labelWidthMm: f.labelWidthMm ?? defaultSettings.labelWidthMm,
  labelHeightMm: f.labelHeightMm ?? defaultSettings.labelHeightMm,
  barcodeWidthMm: f.barcodeWidthMm ?? defaultSettings.barcodeWidthMm,
  barcodeHeightMm: f.barcodeHeightMm ?? defaultSettings.barcodeHeightMm,
  marginTopMm: f.marginTopMm ?? defaultSettings.marginTopMm,
  marginBottomMm: f.marginBottomMm ?? f.marginTopMm ?? defaultSettings.marginBottomMm,
  marginLeftMm: f.marginLeftMm ?? defaultSettings.marginLeftMm,
  marginRightMm: f.marginRightMm ?? f.marginLeftMm ?? defaultSettings.marginRightMm,
  verticalGapMm: f.verticalGapMm ?? defaultSettings.verticalGapMm,
  horizontalGapMm: f.horizontalGapMm ?? defaultSettings.horizontalGapMm,
  noOfColumns: f.noOfColumns != null ? f.noOfColumns : (schemeToColumns(f.schemeName) ?? defaultSettings.noOfColumns),
  codeType: f.codeType || defaultSettings.codeType,
  showBarcodeNumber: f.showBarcodeNumber !== false,
  visibleFields: f.visibleFields ?? defaultSettings.visibleFields,
  schemeName: f.schemeName ?? defaultSettings.schemeName,
  fieldLayout: f.fieldLayout ?? defaultSettings.fieldLayout,
  fieldSettings: f.fieldLayout ?? defaultSettings.fieldSettings,
  fontSizePt: f.fontSizePt ?? defaultSettings.fontSizePt,
});

const LabelSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formats, setFormats] = useState([]);
  const [selectedFormatId, setSelectedFormatId] = useState(null);
  const [settings, setSettings] = useState(defaultSettings);
  const [error, setError] = useState('');
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [showFormView, setShowFormView] = useState(false);
  const [activeFormatId, setActiveFormatId] = useState(null);
  const [activeFormatName, setActiveFormatName] = useState('');
  const [savingActive, setSavingActive] = useState(false);
  const [saveNameDialogOpen, setSaveNameDialogOpen] = useState(false);
  const [saveNameValue, setSaveNameValue] = useState('');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [fieldSettingsDialogOpen, setFieldSettingsDialogOpen] = useState(false);
  const [draftFieldMap, setDraftFieldMap] = useState(() => buildDraftFieldMap([]));
  const [expandedFieldKey, setExpandedFieldKey] = useState(null);
  const barcodeCanvasRefs = useRef({});
  const previewPrintRef = useRef(null);
  const initialLoadDone = useRef(false);

  const loadFormats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const list = await barcodeSettingsService.getFormats();
      setFormats(Array.isArray(list) ? list : []);
      setFormats(Array.isArray(list) ? list : []);
      // Auto-selection on load removed as requested. User will choose or click Edit.
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load formats');
      toast.error('Failed to load formats');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompanySettings = useCallback(async () => {
    try {
      const data = await companySettingsService.getSettings();
      setActiveFormatId(data.activeBarcodeFormatId ?? null);
      setActiveFormatName(data.activeBarcodeFormatName || '');
    } catch (err) {
      console.warn('Failed to load company settings for active format:', err);
    }
  }, []);

  useEffect(() => {
    loadFormats();
    loadCompanySettings();
  }, [loadCompanySettings]);

  const handleUseFormat = useCallback(async (f) => {
    try {
      setSavingActive(true);
      await companySettingsService.updateSettings({
        activeBarcodeFormatId: f.id,
        activeBarcodeFormatName: f.formatName || `Format ${f.id}`,
      });
      setActiveFormatId(f.id);
      setActiveFormatName(f.formatName || `Format ${f.id}`);
      toast.success('"' + (f.formatName || `Format ${f.id}`) + '" is now the label format in use. It will be used on the Barcode Printing page.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSavingActive(false);
    }
  }, []);

  const openPreviewForFormat = useCallback((f) => {
    setSelectedFormatId(f.id);
    setSettings(mapFormatToSettings(f));
    setPreviewDialogOpen(true);
  }, []);

  const openEditForFormat = useCallback((f) => {
    setSelectedFormatId(f.id);
    setSettings(mapFormatToSettings(f));
    setSaveSuccessMessage('');
    setShowFormView(true);
  }, []);

  const handleAddNewFormat = useCallback(() => {
    setSelectedFormatId(null);
    setSettings({ ...defaultSettings, id: null, formatName: '' });
    setSaveSuccessMessage('');
    setShowFormView(true);
  }, []);

  const handleSelectFormat = (formatId) => {
    setSaveSuccessMessage('');
    if (formatId === '' || formatId === 'new') {
      setSelectedFormatId(null);
      setSettings({ ...defaultSettings, id: null, formatName: '' });
      return;
    }
    const id = Number(formatId);
    const f = formats.find((x) => x.id === id);
    if (f) {
      setSelectedFormatId(id);
      setSettings(mapFormatToSettings(f));
    }
  };

  const buildSavePayload = (formatName) => {
    const selectedFields = getFieldSettingsList();
    const visibleFieldsFromSettings =
      selectedFields.length > 0 ? selectedFields.map((f) => f.fieldKey).join(',') : '';
    const raw = settings.fieldSettings || settings.fieldLayout;
    const fieldLayoutJson =
      selectedFields.length > 0
        ? typeof raw === 'string'
          ? raw
          : JSON.stringify(raw || [])
        : '[]';
    return {
      formatName: formatName || settings.formatName || '',
      schemeName: settings.schemeName || null,
      labelWidthMm: settings.labelWidthMm,
      labelHeightMm: settings.labelHeightMm,
      paperSize: settings.paperSize || 'A4',
      barcodeWidthMm: settings.barcodeWidthMm,
      barcodeHeightMm: settings.barcodeHeightMm,
      marginTopMm: settings.marginTopMm,
      marginBottomMm: settings.marginBottomMm ?? settings.marginTopMm,
      marginLeftMm: settings.marginLeftMm,
      marginRightMm: settings.marginRightMm ?? settings.marginLeftMm,
      verticalGapMm: settings.verticalGapMm,
      horizontalGapMm: settings.horizontalGapMm,
      noOfColumns: settings.noOfColumns != null ? settings.noOfColumns : 5,
      codeType: settings.codeType,
      showBarcodeNumber: settings.showBarcodeNumber,
      visibleFields: visibleFieldsFromSettings,
      fieldLayout: fieldLayoutJson,
      fontSizePt: settings.fontSizePt,
    };
  };

  const handleOpenSaveDialog = () => {
    setSaveNameValue(settings.formatName || '');
    setSaveSuccessMessage('');
    setSaveNameDialogOpen(true);
  };

  /** Update existing format: save current settings to DB without name dialog. */
  const handleUpdateSettings = async () => {
    const name = (settings.formatName || '').trim();
    if (!name) {
      toast.error('Format name is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = buildSavePayload(name);
      payload.id = selectedFormatId;
      const saved = await barcodeSettingsService.saveSettings(payload);
      const storedName = saved.formatName != null ? String(saved.formatName).trim() : name;
      setSaveSuccessMessage('Format "' + storedName + '" updated successfully.');
      setSelectedFormatId(saved.id);
      setSettings(mapFormatToSettings(saved));
      await loadFormats();
      toast.success('Updated "' + storedName + '"');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    const name = saveNameValue.trim();
    if (!name) {
      toast.error('Please enter a name for this format');
      return;
    }
    try {
      setSaving(true);
      const payload = buildSavePayload(name);
      if (selectedFormatId != null) payload.id = selectedFormatId;
      const saved = await barcodeSettingsService.saveSettings(payload);
      const storedName = saved.formatName != null ? String(saved.formatName).trim() : name;
      setSaveNameDialogOpen(false);
      setSaveNameValue('');
      setError('');
      setSaveSuccessMessage('Format "' + storedName + '" saved successfully. You can select it from the dropdown or use it on the Barcode Printing page.');
      setSelectedFormatId(saved.id);
      setSettings(mapFormatToSettings(saved));
      await loadFormats();
      toast.success('Saved as "' + storedName + '"');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  /** Parse stored field_layout / fieldSettings JSON (includes show=false if present). */
  const getStoredFieldSettingsRaw = () => {
    if (settings.fieldSettings) {
      try {
        const arr = JSON.parse(settings.fieldSettings);
        if (Array.isArray(arr)) return arr;
      } catch (e) { /* ignore */ }
    }
    if (settings.fieldLayout) {
      try {
        const arr = JSON.parse(settings.fieldLayout);
        if (Array.isArray(arr) && arr.length > 0) return arr;
      } catch (e) { /* ignore */ }
    }
    return [];
  };

  /** Only fields stored in DB (user selected + show=true). No default list. */
  const getFieldSettingsList = () => {
    if (settings.fieldSettings) {
      try {
        const arr = JSON.parse(settings.fieldSettings);
        if (Array.isArray(arr)) {
          return arr
            .map((item) => ({
              ...defaultFieldSetting(item.fieldKey, item.order ?? 0),
              ...item,
              show: item.show !== false,
            }))
            .filter((f) => f.show === true);
        }
      } catch (e) { }
    }
    if (settings.fieldLayout) {
      try {
        const arr = JSON.parse(settings.fieldLayout);
        if (Array.isArray(arr) && arr.length > 0) {
          return arr
            .filter((item) => item.show !== false)
            .map((item, idx) => ({
              ...defaultFieldSetting(item.fieldKey, item.order ?? idx),
              ...item,
              show: true,
            }));
        }
      } catch (e) { }
    }
    return [];
  };

  const updateDraftField = (fieldKey, patch) => {
    setDraftFieldMap((prev) => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], ...patch, fieldKey },
    }));
  };

  const toggleDraftFieldShow = (fieldKey, show) => {
    updateDraftField(fieldKey, { show });
    if (show) setExpandedFieldKey(fieldKey);
  };

  const applyFieldPreset = (preset) => {
    const keySet = new Set(preset.keys);
    let textIdx = 0;
    setDraftFieldMap((prev) => {
      const next = { ...prev };
      FIELD_OPTIONS.forEach((opt, idx) => {
        const on = keySet.has(opt.key);
        const base = next[opt.key] || defaultFieldSetting(opt.key, idx);
        if (on) {
          const top = presetTopMm(opt.key, opt.key === 'QR' ? 0 : textIdx);
          if (opt.key !== 'QR') textIdx += 1;
          next[opt.key] = { ...base, show: true, top, fieldKey: opt.key };
        } else {
          next[opt.key] = { ...base, show: false, fieldKey: opt.key };
        }
      });
      return next;
    });
    setExpandedFieldKey(preset.keys[0] || null);
    toast.success(`Applied "${preset.label}"`);
  };

  const clearAllDraftFields = () => {
    setDraftFieldMap(buildDraftFieldMap([]));
    setExpandedFieldKey(null);
  };

  const applyAllFieldSettings = () => {
    const list = draftMapToFieldList(draftFieldMap);
    updateSetting('fieldSettings', list.length ? JSON.stringify(list) : null);
    setFieldSettingsDialogOpen(false);
    toast.success(
      list.length
        ? `${list.length} field${list.length === 1 ? '' : 's'} on label. Click Update to save the format.`
        : 'All fields removed from label.'
    );
  };

  const openFieldSettingsDialog = () => {
    const stored = getStoredFieldSettingsRaw();
    const draft = buildDraftFieldMap(stored);
    setDraftFieldMap(draft);
    const firstOn = FIELD_OPTIONS.find((o) => draft[o.key]?.show)?.key ?? null;
    setExpandedFieldKey(firstOn);
    setFieldSettingsDialogOpen(true);
  };

  const draftSelectedCount = () =>
    FIELD_OPTIONS.filter((o) => draftFieldMap[o.key]?.show === true).length;

  /** Only fields stored in DB with show=true are shown on the label. Uses full field settings (font size, alignment, order). */
  const getFieldLayout = () => {
    const list = getFieldSettingsList();
    return list
      .map((item, idx) => ({
        ...item,
        fieldKey: item.fieldKey,
        show: item.show !== false,
        alignment: item.alignment || 'Centre',
        order: item.order ?? idx,
      }))
      .filter((f) => f.show)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  };

  const formatDateMMYY = (dateVal) => {
    if (dateVal == null) return '';

    let d = null;
    if (dateVal instanceof Date) {
      d = dateVal;
    } else if (typeof dateVal === 'string' || typeof dateVal === 'number') {
      const tmp = new Date(dateVal);
      if (!Number.isNaN(tmp.getTime())) d = tmp;
    } else if (typeof dateVal === 'object') {
      if (Array.isArray(dateVal) && dateVal.length >= 3) {
        const dTmp = new Date(dateVal[0], dateVal[1] - 1, dateVal[2]);
        if (!Number.isNaN(dTmp.getTime())) d = dTmp;
      } else {
        const year = typeof dateVal.year === 'number' ? dateVal.year : null;
        const monthRaw =
          typeof dateVal.monthValue === 'number'
            ? dateVal.monthValue
            : typeof dateVal.month === 'number'
              ? dateVal.month
              : null;
        const dayRaw =
          typeof dateVal.dayOfMonth === 'number'
            ? dateVal.dayOfMonth
            : typeof dateVal.day === 'number'
              ? dateVal.day
              : null;
        if (year != null && monthRaw != null && dayRaw != null) {
          const tmp = new Date(year, monthRaw - 1, dayRaw);
          if (!Number.isNaN(tmp.getTime())) d = tmp;
        }
      }
    }

    if (!d || typeof d.getTime !== 'function' || Number.isNaN(d.getTime())) return '';

    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return month + year;
  };

  const getFieldValue = (product, fieldKey) => {
    switch (fieldKey) {
      case 'COMPANY_NAME':
        return getCurrentUser()?.companyName || 'Company Name';
      case 'NAME':
        return product.name || '';
      case 'SKU':
        return product.sku || '';
      case 'SKU_LAST3': {
        // Return last 4 characters of SKU (e.g., "SKU0001" -> "0001")
        const sku = product.sku || '';
        return sku.slice(-4);
      }
      case 'BARCODE':
        return product.barcode || '';
      case 'MRP':
        return formatPrice(product.mrp ?? product.sellingPrice);
      case 'SELLING_PRICE':
        return formatPrice(product.sellingPrice ?? product.mrp);
      case 'DATE_MMYY': {
        const dateVal = product.updatedAt ?? product.updated_at ?? product.createdAt ?? product.created_at;
        return formatDateMMYY(dateVal);
      }
      case 'STOCK_DATE_MMYY': {
        // Uses stockInDate which is ONLY updated when stock is added (not on sales)
        const stockDate = product.stockInDate ?? product.stock_in_date ?? product.createdAt ?? product.created_at;
        return formatDateMMYY(stockDate);
      }
      case 'SUPPLIER_CODE':
        return 'TSTSUP12';
      case 'PRODUCT_CODE':
        return 'PC123';
      case 'HSN_CODE':
        return product.hsnCode || '';
      case 'CATEGORY':
        return product.category?.name || '';
      default:
        return '';
    }
  };

  function formatPrice(val) {
    if (val == null) return '';
    const dec = settings.decimalsForPrice ?? 0;
    let prefix = settings.currency || 'Rs.';
    if (prefix === 'Rs') prefix = 'Rs.';
    return prefix + Number(val).toFixed(dec);
  }

  const expandedLabelList = React.useMemo(() => {
    // Show multiple sample labels so grid preview looks realistic
    return Array.from({ length: 12 }).map((_, i) => ({ ...sampleProduct, id: `SAMPLE-${i}` }));
  }, []);

  const renderOneLabel = (product, labelKey, forPreview = false) => {
    const layout = getFieldLayout();
    const marginLeftMm = Number(settings.marginLeftMm) || 0;
    const marginTopMm = Number(settings.marginTopMm) || 0;
    const marginLeftPx = marginLeftMm * MM_TO_PX;
    const marginTopPx = marginTopMm * MM_TO_PX;
    const labelWidthMmVal = Number(settings.labelWidthMm) || 39;
    const labelHeightMmVal = Number(settings.labelHeightMm) || 35;
    const labelW = labelWidthMmVal * MM_TO_PX;
    const labelH = labelHeightMmVal * MM_TO_PX;
    const isSmallLabel = labelHeightMmVal <= 15;
    const barcodeWidthMmVal = Math.max(1, Number(settings.barcodeWidthMm) || 25);
    const barcodeHeightMmVal = Math.max(1, Number(settings.barcodeHeightMm) || 15);
    const barcodeWidthPx = Math.max(8, barcodeWidthMmVal * MM_TO_PX);
    const barcodeHeightPx = Math.max(8, barcodeHeightMmVal * MM_TO_PX);
    const baseFontSizePt = settings.fontSizePt ?? 8;
    const baseFontSizePx = Math.max(6, Math.min(baseFontSizePt * 1.2, labelH * 0.8));

    const qrField = layout.find((f) => f.fieldKey === 'QR');
    const showImage =
      (settings.codeType === 'QR' || settings.codeType === 'BARCODE') &&
      !!qrField &&
      qrField.show !== false;
    const textItems = layout.filter((f) => f.fieldKey !== 'QR' && f.show !== false);

    const paperSx = forPreview
      ? {
        width: `${labelWidthMmVal}mm`,
        height: `${labelHeightMmVal}mm`,
        minHeight: `${labelHeightMmVal}mm`,
        padding: `${marginTopMm}mm ${marginLeftMm}mm`,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: isSmallLabel ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        position: 'relative',
        fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif",
      }
      : {
        width: labelW,
        height: labelH,
        minHeight: labelH,
        p: `${marginTopPx}px ${marginLeftPx}px`,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: isSmallLabel ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        position: 'relative',
        fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif",
      };

    // Position barcode/QR image using its field settings (left/top/width/height in mm)
    const qrLeftMm = qrField?.left != null ? qrField.left : 0;
    const qrTopMm = qrField?.top != null ? qrField.top : 0;
    const qrWidthMm = qrField?.width != null ? qrField.width : barcodeWidthMmVal;
    const qrHeightMm = qrField?.height != null ? qrField.height : barcodeHeightMmVal;

    const barcodeBoxSx = {
      position: 'absolute',
      left: `${qrLeftMm}mm`,
      top: `${qrTopMm}mm`,
      width: `${qrWidthMm}mm`,
      height: `${qrHeightMm}mm`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    return (
      <Paper key={labelKey} variant="outlined" sx={paperSx}>
        {showImage && (
          <Box sx={barcodeBoxSx}>
            {settings.codeType === 'QR' ? (
              <QRCodeSVG
                value={product.barcode || String(product.id)}
                size={Math.min(barcodeWidthPx, barcodeHeightPx)}
                level="M"
                style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
              />
            ) : (
              <svg
                ref={(el) => {
                  if (el) barcodeCanvasRefs.current[`${labelKey}-qr`] = el;
                }}
                data-label-key={labelKey}
                data-value={product.barcode || String(product.id)}
                style={{ display: 'block', width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
              />
            )}
          </Box>

        )}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          {textItems.map((item, idx) => {
            const text = getFieldValue(product, item.fieldKey);
            if (!text) return null;
            const textAlign = (item.alignment || 'Centre').toLowerCase();
            const textAlignMap = { left: 'left', centre: 'center', center: 'center', right: 'right' };
            const itemFontPt = item.fontSize ?? baseFontSizePt;
            const itemFontPx = Math.max(6, Math.min(itemFontPt * 1.2, labelH * 0.8));

            const posSx = {
              position: 'absolute',
              left: `${item.left ?? 4}mm`,
              top: `${item.top ?? 2}mm`,
              width: `${item.width ?? 35}mm`,
              textAlign: textAlignMap[textAlign] || 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: `${itemFontPx}px`,
              fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif",
            };

            if (item.fieldKey === 'MRP' || item.fieldKey === 'mrp') {
              const { whiteSpace, overflow, textOverflow, ...mrpPosSx } = posSx;
              return (
                <Box
                  key={idx}
                  sx={{
                    ...mrpPosSx,
                    lineHeight: 1.1,
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: `${Math.max(6, itemFontPx * 0.9)}px`, display: 'block', fontWeight: 'bold', fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif" }}>
                    MRP
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: `${itemFontPx}px`, fontWeight: 'bold', fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif" }}>
                    {text}
                  </Typography>
                </Box>
              );
            }

            return (
              <Box
                key={idx}
                sx={{
                  ...posSx,
                  fontWeight: 'bold',
                }}
              >
                {text}
              </Box>
            );
          })}
        </Box>
      </Paper>
    );
  };

  useEffect(() => {
    if (settings.codeType !== 'BARCODE') return;
    if (!previewDialogOpen) return;
    const drawBarcodes = () => {
      const layout = getFieldLayout();
      const qrField = layout.find((f) => f.fieldKey === 'QR');
      const customHeightMm = qrField?.height != null ? qrField.height : (settings.barcodeHeightMm || 15);
      const customWidthMm = qrField?.width != null ? qrField.width : (settings.barcodeWidthMm || 25);
      const barcodeHeightPx = Math.max(20, customHeightMm * MM_TO_PX);
      const barWidth = customWidthMm > 30 ? 2.0 : 1.5;

      expandedLabelList.forEach((product, i) => {
        const labelKey = `sample-label-${i}`;
        const canvas = barcodeCanvasRefs.current[`${labelKey}-qr`];
        if (!canvas) return;
        const value = product.barcode || String(product.id);
        if (!value) return;
        try {
          JsBarcode(canvas, value, {
            format: 'CODE128',
            width: barWidth,
            height: barcodeHeightPx,
            displayValue: false,
            margin: 0,
          });
        } catch (e) {
          console.warn('JsBarcode error:', e);
        }
      });
    };


    const t = setTimeout(drawBarcodes, 150);
    return () => clearTimeout(t);
  }, [previewDialogOpen, expandedLabelList, settings.codeType, settings.barcodeWidthMm, settings.barcodeHeightMm]);

  const noOfColumns = Math.max(1, parseInt(settings.noOfColumns, 10) || 5);
  const labelWidthMm = Number(settings.labelWidthMm) || 39;
  const labelHeightMm = Number(settings.labelHeightMm) || 35;
  const horizontalGapMm = Number(settings.horizontalGapMm) || 0;
  const verticalGapMm = Number(settings.verticalGapMm) || 1;

  const paperSizeKey = settings.paperSize || 'A4';
  const paperSizeDef = PAPER_SIZES.find((p) => p.value === paperSizeKey) || PAPER_SIZES[0];
  const paperWidthMm = paperSizeDef.widthMm;
  const paperHeightMm = paperSizeDef.heightMm;
  const marginLeftMm = Number(settings.marginLeftMm) || 0;
  const marginTopMm = Number(settings.marginTopMm) || 0;
  const marginRightMm = marginLeftMm;
  const marginBottomMm = marginTopMm;

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }


  return (
    <Layout>
      {!showFormView ? (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Label settings (Barcode / QR)
              </Typography>
                
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNewFormat}>
              Add format
            </Button>
          </Box>
          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {/* Label format in use notification removed as requested */}
          {formats.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>No label formats yet. Create one to get started.</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNewFormat}>
                Add format
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {formats.map((f) => (
                <Grid item xs={12} sm={6} md={4} key={f.id}>
                  <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {f.formatName || `Format ${f.id}`}
                      </Typography>
                      {activeFormatId === f.id && (
                        <Typography component="span" variant="caption" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircleIcon fontSize="small" /> In use
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      ID: {f.id} · {f.codeType || 'BARCODE'} · {f.labelWidthMm ?? '-'}×{f.labelHeightMm ?? '-'} mm
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<PreviewIcon />} onClick={() => openPreviewForFormat(f)}>
                        Preview
                      </Button>
                      <Button size="small" variant="outlined" color="primary" startIcon={<EditIcon />} onClick={() => openEditForFormat(f)}>
                        Edit
                      </Button>
                      <Button size="small" variant="contained" startIcon={<CheckCircleIcon />} onClick={() => handleUseFormat(f)} disabled={savingActive || activeFormatId === f.id}>
                        Use it
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => { setShowFormView(false); setSaveSuccessMessage(''); }}>
              Back to list
            </Button>
            <Typography variant="h5" fontWeight="bold">
              Label settings (Barcode / QR)
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {saveSuccessMessage && (
            <Alert severity="success" onClose={() => setSaveSuccessMessage('')} sx={{ mb: 2 }}>
              {saveSuccessMessage}
            </Alert>
          )}

          <Paper sx={{ p: 2 }}>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select format</InputLabel>
                  <Select
                    value={selectedFormatId != null ? selectedFormatId : 'new'}
                    label="Select format"
                    onChange={(e) => handleSelectFormat(e.target.value)}
                  >
                    <MenuItem value="new">Create new format</MenuItem>
                    {formats.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.formatName || `Format ${f.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Label settings
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              All units in millimetre (mm)
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Scheme</InputLabel>
                  <Select
                    value={settings.schemeName || '4x4'}
                    label="Scheme"
                    onChange={(e) => {
                      const value = e.target.value;
                      const cols = schemeToColumns(value);
                      updateSetting('schemeName', value);
                      if (cols != null) updateSetting('noOfColumns', cols);
                    }}
                  >
                    {SCHEME_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Paper size</InputLabel>
                  <Select
                    value={settings.paperSize || 'A4'}
                    label="Paper size"
                    onChange={(e) => updateSetting('paperSize', e.target.value)}
                  >
                    {PAPER_SIZES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Label width (mm)"
                  type="number"
                  value={settings.labelWidthMm === 0 ? '' : (settings.labelWidthMm ?? '')}
                  onChange={(e) => updateSetting('labelWidthMm', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Label height (mm)"
                  type="number"
                  value={settings.labelHeightMm === 0 ? '' : (settings.labelHeightMm ?? '')}
                  onChange={(e) => updateSetting('labelHeightMm', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                />
              </Grid>
              {false && (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Barcode width (mm)"
                    type="number"
                    value={settings.barcodeWidthMm ?? ''}
                    onChange={(e) => updateSetting('barcodeWidthMm', parseFloat(e.target.value) || null)}
                    inputProps={{ min: 1, step: 0.5 }}
                  />
                </Grid>
              )}
              {false && (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Barcode height (mm)"
                    type="number"
                    value={settings.barcodeHeightMm ?? ''}
                    onChange={(e) => updateSetting('barcodeHeightMm', parseFloat(e.target.value) || null)}
                    inputProps={{ min: 1, step: 0.5 }}
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Left margin (mm)"
                  type="number"
                  value={settings.marginLeftMm === 0 ? '' : (settings.marginLeftMm ?? '')}
                  onChange={(e) => updateSetting('marginLeftMm', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Top margin (mm)"
                  type="number"
                  value={settings.marginTopMm === 0 ? '' : (settings.marginTopMm ?? '')}
                  onChange={(e) => updateSetting('marginTopMm', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Horizontal gap (mm)"
                  type="number"
                  value={settings.horizontalGapMm === 0 ? '' : (settings.horizontalGapMm ?? '')}
                  onChange={(e) => updateSetting('horizontalGapMm', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Vertical gap (mm)"
                  type="number"
                  value={settings.verticalGapMm === 0 ? '' : (settings.verticalGapMm ?? '')}
                  onChange={(e) => updateSetting('verticalGapMm', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="No of columns"
                  type="number"
                  value={settings.noOfColumns === 0 ? '' : (settings.noOfColumns ?? '')}
                  onChange={(e) => updateSetting('noOfColumns', e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 5))}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                  inputProps={{ min: 1, max: 10 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Currency"
                  value={settings.currency ?? 'Rs'}
                  onChange={(e) => updateSetting('currency', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel>Barcode type</FormLabel>
                  <RadioGroup
                    row
                    value={settings.codeType || 'BARCODE'}
                    onChange={(e) => updateSetting('codeType', e.target.value)}
                  >
                    <FormControlLabel value="BARCODE" control={<Radio />} label="Code 128" />
                    <FormControlLabel value="QR" control={<Radio />} label="QR Code" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
              Fields on label
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Tick the fields you want, set position and alignment in one place, then click Update to save the format.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                {getFieldSettingsList().length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No fields yet — open Configure fields and tick what you need (or use a quick layout).
                  </Typography>
                ) : (
                  getFieldSettingsList()
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map((f) => {
                      const opt = FIELD_OPTIONS.find((o) => o.key === f.fieldKey);
                      return (
                        <Chip
                          key={f.fieldKey}
                          size="small"
                          label={opt ? opt.label : f.fieldKey}
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })
                )}
              </Box>
              <Button variant="contained" size="small" startIcon={<SettingsIcon />} onClick={openFieldSettingsDialog}>
                Configure fields
              </Button>
            </Paper>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
              {selectedFormatId != null ? (
                <Button
                  variant="outlined"
                  startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                  onClick={handleUpdateSettings}
                  disabled={saving}
                >
                  Update
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                  onClick={handleOpenSaveDialog}
                  disabled={saving}
                >
                  Save
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<PreviewIcon />}
                onClick={() => setPreviewDialogOpen(true)}
              >
                Show preview
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Save format name dialog */}
      <Dialog open={saveNameDialogOpen} onClose={() => !saving && setSaveNameDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save label format</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name for this format"
            placeholder="e.g. 4x4 Thermal, A4 Labels"
            value={saveNameValue}
            onChange={(e) => setSaveNameValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveSettings()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveNameDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />} onClick={handleSaveSettings} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>Preview – Barcode labels ({paperSizeKey})</DialogTitle>
        <DialogContent
          dividers
          ref={previewPrintRef}
          sx={{ padding: 0, overflow: 'auto', display: 'flex', justifyContent: 'flex-start' }}
        >
          <Box
            className="barcode-preview-sheet"
            data-paper-width-mm={paperWidthMm}
            data-paper-height-mm={paperHeightMm}
            sx={{
              width: `${paperWidthMm}mm`,
              minHeight: `${paperHeightMm}mm`,
              padding: 0,
              margin: 0,
              bgcolor: '#fff',
              boxSizing: 'border-box',
            }}
          >
            <Box
              className="barcode-preview-grid"
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${noOfColumns}, ${labelWidthMm}mm)`,
                gap: `${verticalGapMm}mm ${horizontalGapMm}mm`,
                justifyContent: 'start',
                alignContent: 'start',
                boxSizing: 'border-box',
                paddingTop: `${Number(marginTopMm) || 0}mm`,
                paddingRight: `${Number(marginRightMm) || 0}mm`,
                paddingBottom: `${Number(marginBottomMm) || 0}mm`,
                paddingLeft: `${Number(marginLeftMm) || 0}mm`,
                margin: 0,
              }}
            >
              {expandedLabelList.map((product, i) => renderOneLabel(product, `sample-label-${i}`, true))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={() => setPreviewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field settings dialog — bulk select + configure, one Apply */}
      <Dialog
        open={fieldSettingsDialogOpen}
        onClose={() => setFieldSettingsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          Configure label fields
          <Typography variant="body2" color="text.secondary" fontWeight="normal">
            Tick fields below, adjust position if needed, then Apply — no need to save each field separately.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Quick layouts
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {FIELD_PRESETS.map((preset) => (
              <Chip
                key={preset.id}
                label={preset.label}
                onClick={() => applyFieldPreset(preset)}
                clickable
                color="primary"
                variant="outlined"
              />
            ))}
            <Chip label="Clear all" onClick={clearAllDraftFields} clickable variant="outlined" />
          </Stack>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Select fields on label ({draftSelectedCount()} selected)
          </Typography>
          <Grid container spacing={0.5} sx={{ mb: 2 }}>
            {FIELD_OPTIONS.map((opt) => {
              const on = draftFieldMap[opt.key]?.show === true;
              return (
                <Grid item xs={12} sm={6} key={opt.key}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      width: '100%',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: on ? 'primary.50' : 'transparent',
                      border: '1px solid',
                      borderColor: on ? 'primary.light' : 'divider',
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={on}
                        onChange={(e) => toggleDraftFieldShow(opt.key, e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                </Grid>
              );
            })}
          </Grid>

          {draftSelectedCount() > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Position &amp; alignment (mm) — expand a field to fine-tune
              </Typography>
              {FIELD_OPTIONS.filter((o) => draftFieldMap[o.key]?.show === true).map((opt) => {
                const fs = draftFieldMap[opt.key];
                const isQr = opt.key === 'QR';
                return (
                  <Accordion
                    key={opt.key}
                    expanded={expandedFieldKey === opt.key}
                    onChange={(_, exp) => setExpandedFieldKey(exp ? opt.key : null)}
                    disableGutters
                    sx={{ mb: 0.5, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" fontWeight={600}>
                        {opt.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        L {fs.left ?? '—'} · T {fs.top ?? '—'}
                        {isQr ? ` · ${fs.width ?? '—'}×${fs.height ?? '—'} mm` : ` · ${fs.alignment || 'Centre'}`}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Left"
                            type="number"
                            value={fs.left === 0 ? '' : (fs.left ?? '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              updateDraftField(opt.key, { left: val === '' ? '' : parseFloat(val) });
                            }}
                            onWheel={(e) => e.target.blur()}
                            sx={{
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                              '& input[type=number]': { MozAppearance: 'textfield' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Top"
                            type="number"
                            value={fs.top === 0 ? '' : (fs.top ?? '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              updateDraftField(opt.key, { top: val === '' ? '' : parseFloat(val) });
                            }}
                            onWheel={(e) => e.target.blur()}
                            sx={{
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                              '& input[type=number]': { MozAppearance: 'textfield' },
                            }}
                          />
                        </Grid>
                        {isQr ? (
                          <>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Width"
                                type="number"
                                value={fs.width === 0 ? '' : (fs.width ?? '')}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateDraftField(opt.key, { width: val === '' ? '' : parseFloat(val) });
                                }}
                                onWheel={(e) => e.target.blur()}
                                sx={{
                                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                  '& input[type=number]': { MozAppearance: 'textfield' },
                                }}
                              />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Height"
                                type="number"
                                value={fs.height === 0 ? '' : (fs.height ?? '')}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateDraftField(opt.key, { height: val === '' ? '' : parseFloat(val) });
                                }}
                                onWheel={(e) => e.target.blur()}
                                sx={{
                                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                  '& input[type=number]': { MozAppearance: 'textfield' },
                                }}
                              />
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Font size"
                                type="number"
                                value={fs.fontSize === 0 ? '' : (fs.fontSize ?? '')}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateDraftField(opt.key, { fontSize: val === '' ? '' : (parseInt(val, 10) || 8) });
                                }}
                                onWheel={(e) => e.target.blur()}
                                sx={{
                                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                  '& input[type=number]': { MozAppearance: 'textfield' },
                                }}
                              />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Alignment</InputLabel>
                                <Select
                                  value={fs.alignment || 'Centre'}
                                  label="Alignment"
                                  onChange={(e) => updateDraftField(opt.key, { alignment: e.target.value })}
                                >
                                  <MenuItem value="Left">Left</MenuItem>
                                  <MenuItem value="Centre">Centre</MenuItem>
                                  <MenuItem value="Right">Right</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button startIcon={<CloseIcon />} onClick={() => setFieldSettingsDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<DoneIcon />} onClick={applyAllFieldSettings}>
            Apply {draftSelectedCount() > 0 ? `(${draftSelectedCount()} fields)` : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default LabelSettingsPage;

